import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TcardsBrandLogo from '../TcardsBrandLogo';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { TCARDS_HOME_URL } from '../miniwebsite/miniWebsitePaths';
import {
  CONTACT_PATH,
  DOWNLOAD_PATH,
  FIELD_TEAM_SOLUTION_SLUGS,
  FEATURES_SECTION_ID,
  HOW_SECTION_ID,
  INDUSTRIES_SECTION_ID,
  LOGIN_SECTION_ID,
  PRICING_SECTION_ID,
  SOLUTIONS_SECTION_ID,
  fieldTeamHomePath,
  fieldTeamSectionUrl,
  fieldTeamSolutionPath,
  isFieldTeamHomePath,
  scrollToFieldTeamSection,
} from './fieldTeamRoutes';

type Props = {
  activePath?: string;
  onGetStarted: () => void;
};

export const FieldTeamNav: React.FC<Props> = ({ activePath, onGetStarted }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const home = fieldTeamHomePath(location.pathname);

  const handleSectionClick = (sectionId: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    setOpen(false);
    if (isFieldTeamHomePath(location.pathname)) {
      e.preventDefault();
      navigate({ pathname: home, hash: `#${sectionId}` });
      window.setTimeout(() => scrollToFieldTeamSection(sectionId), 0);
    }
  };

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <header className="dcl-nav">
      <TcardsBrandLogo
        href={TCARDS_HOME_URL}
        className="dcl-nav-brand"
        tagline={t('ftl_nav_tagline')}
        taglineClassName="dcl-nav-tagline"
        size={36}
      />
      <nav className="dcl-nav-links" aria-label="Main">
        <div className="ftl-nav-solutions">
          <Link
            to={fieldTeamSectionUrl(SOLUTIONS_SECTION_ID, location.pathname)}
            className={`dcl-nav-link${activePath === home ? ' dcl-nav-link--active' : ''}`}
            onClick={handleSectionClick(SOLUTIONS_SECTION_ID)}
          >
            {t('ftl_nav_solutions')}
          </Link>
          <div className="ftl-nav-solutions-menu">
            {FIELD_TEAM_SOLUTION_SLUGS.map((slug) => (
              <Link key={slug} to={fieldTeamSolutionPath(slug)} onClick={() => setOpen(false)}>
                {t(`ftl_sol_${slug}_nav`)}
              </Link>
            ))}
          </div>
        </div>
        <Link to={fieldTeamSectionUrl(FEATURES_SECTION_ID, location.pathname)} className="dcl-nav-link" onClick={handleSectionClick(FEATURES_SECTION_ID)}>
          {t('ftl_nav_features')}
        </Link>
        <Link to={fieldTeamSectionUrl(INDUSTRIES_SECTION_ID, location.pathname)} className="dcl-nav-link" onClick={handleSectionClick(INDUSTRIES_SECTION_ID)}>
          {t('ftl_nav_industries')}
        </Link>
        <Link to={fieldTeamSectionUrl(PRICING_SECTION_ID, location.pathname)} className="dcl-nav-link" onClick={handleSectionClick(PRICING_SECTION_ID)}>
          {t('ftl_nav_pricing')}
        </Link>
        <a href={DOWNLOAD_PATH} className="dcl-nav-link">{t('ftl_nav_download')}</a>
        <Link to={CONTACT_PATH} className="dcl-nav-link">{t('ftl_nav_contact')}</Link>
      </nav>
      <div className="dcl-nav-row">
        <div className="dcl-nav-actions">
          <LanguageSwitcher compact className="dcl-lang-select" />
          <Link
            to={fieldTeamSectionUrl(LOGIN_SECTION_ID, location.pathname)}
            className="dcl-btn dcl-btn--ghost dcl-btn--sm"
            onClick={handleSectionClick(LOGIN_SECTION_ID)}
          >
            {t('ftl_cta_signin')}
          </Link>
          <button type="button" className="dcl-btn dcl-btn--primary dcl-btn--sm dcl-btn--pill" onClick={onGetStarted}>
            {t('ftl_cta_signup_free')}
          </button>
        </div>
        <button
          type="button"
          className="dcl-menu-btn"
          aria-label={open ? t('dcl_nav_close_menu', { defaultValue: 'Close menu' }) : t('dcl_nav_open_menu', { defaultValue: 'Open menu' })}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? '✕' : '☰'}
        </button>
      </div>
      <div className={`dcl-nav-mobile${open ? ' dcl-nav-mobile--open' : ''}`}>
        <div className="dcl-nav-mobile-lang">
          <LanguageSwitcher className="dcl-lang-select dcl-lang-select--block" />
        </div>
        <Link to={fieldTeamSectionUrl(SOLUTIONS_SECTION_ID, location.pathname)} className="dcl-nav-link" onClick={handleSectionClick(SOLUTIONS_SECTION_ID)}>
          {t('ftl_nav_solutions')}
        </Link>
        {FIELD_TEAM_SOLUTION_SLUGS.map((slug) => (
          <Link key={slug} to={fieldTeamSolutionPath(slug)} className="dcl-nav-link" onClick={() => setOpen(false)}>
            {t(`ftl_sol_${slug}_nav`)}
          </Link>
        ))}
        <Link to={fieldTeamSectionUrl(FEATURES_SECTION_ID, location.pathname)} className="dcl-nav-link" onClick={handleSectionClick(FEATURES_SECTION_ID)}>
          {t('ftl_nav_features')}
        </Link>
        <Link to={fieldTeamSectionUrl(INDUSTRIES_SECTION_ID, location.pathname)} className="dcl-nav-link" onClick={handleSectionClick(INDUSTRIES_SECTION_ID)}>
          {t('ftl_nav_industries')}
        </Link>
        <Link to={fieldTeamSectionUrl(HOW_SECTION_ID, location.pathname)} className="dcl-nav-link" onClick={handleSectionClick(HOW_SECTION_ID)}>
          {t('ftl_nav_how')}
        </Link>
        <Link to={fieldTeamSectionUrl(PRICING_SECTION_ID, location.pathname)} className="dcl-nav-link" onClick={handleSectionClick(PRICING_SECTION_ID)}>
          {t('ftl_nav_pricing')}
        </Link>
        <a href={DOWNLOAD_PATH} className="dcl-nav-link" onClick={() => setOpen(false)}>{t('ftl_nav_download')}</a>
        <Link to={CONTACT_PATH} className="dcl-nav-link" onClick={() => setOpen(false)}>{t('ftl_nav_contact')}</Link>
        <Link
          to={fieldTeamSectionUrl(LOGIN_SECTION_ID, location.pathname)}
          className="dcl-btn dcl-btn--ghost dcl-btn--block"
          onClick={handleSectionClick(LOGIN_SECTION_ID)}
        >
          {t('ftl_cta_signin')}
        </Link>
        <button
          type="button"
          className="dcl-btn dcl-btn--primary dcl-btn--block dcl-btn--pill"
          onClick={() => { setOpen(false); onGetStarted(); }}
        >
          {t('ftl_cta_signup_free')}
        </button>
      </div>
    </header>
  );
};

export default FieldTeamNav;
